/**
 * Auth gate for the Outlook task pane.
 *
 * Microsoft account sign-in is the primary path. Email/password remains a
 * fallback for local/dev and hosts where NAA is unavailable. Outlook does not
 * expose self-sign-up because portal activation owns tenant/profile binding.
 */
import { APP_NAME } from '@aira/shared';
import { type FormEvent, useState } from 'react';
import { useAuth } from '../lib/auth';
import { isMicrosoftSignInConfigured, isNestedAppAuthSupported } from '../lib/microsoftAuth';

const inputClass =
  'w-full rounded-lg border border-sf-primary/20 bg-sf-surface px-3 py-2.5 text-sm text-sf-dark placeholder:text-sf-gray/70 transition-shadow focus:outline-none focus:border-sf-primary focus:ring-2 focus:ring-sf-primary/50 disabled:opacity-60';
const labelClass = 'text-[11px] font-semibold uppercase tracking-wide text-sf-gray';

export function SignIn() {
  const { signInWithMicrosoft, signIn, sendPasswordReset, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [microsoftSubmitting, setMicrosoftSubmitting] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const microsoftConfigured = isMicrosoftSignInConfigured();
  const microsoftSupported = isNestedAppAuthSupported();
  const canUseMicrosoft =
    microsoftConfigured && microsoftSupported && !submitting && !microsoftSubmitting;
  const canSubmit =
    Boolean(email.trim()) && Boolean(password) && !submitting && !microsoftSubmitting;
  const fallbackVisible = showFallback || !microsoftConfigured || !microsoftSupported;

  const handleMicrosoftSignIn = async () => {
    if (!canUseMicrosoft) return;
    setNotice(null);
    setMicrosoftSubmitting(true);
    try {
      await signInWithMicrosoft();
    } catch {
      // error surfaced via useAuth().error
    } finally {
      setMicrosoftSubmitting(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setNotice(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
    } catch {
      // error surfaced via useAuth().error
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    setNotice(null);
    if (!email.trim()) {
      setNotice('Enter your email above first, then tap "Forgot password?".');
      return;
    }
    try {
      await sendPasswordReset(email);
      setNotice(`Password reset link sent to ${email.trim()}.`);
    } catch {
      // error surfaced via useAuth().error
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-sf-primary-light via-sf-surface to-sf-surface p-4">
      <div className="w-full max-w-sm flex flex-col gap-5 rounded-2xl border border-sf-primary/10 bg-sf-surface p-6 shadow-xl shadow-sf-primary/10">
        {/* ── Brand header ── */}
        <div className="flex flex-col items-center gap-3 text-center">
          <img src="/aira-logo-circle.svg" alt="Sharefox" className="h-14 w-14 object-contain" />
          <div className="flex flex-col gap-0.5">
            <h1 className="text-lg font-semibold leading-tight text-sf-primary">{APP_NAME}</h1>
            <p className="text-xs text-sf-gray">Sign in with your Outlook account</p>
          </div>
        </div>

        {/* ── Primary Microsoft SSO ── */}
        {microsoftConfigured && (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={handleMicrosoftSignIn}
              disabled={!canUseMicrosoft}
              className="flex items-center justify-center gap-2 rounded-lg border border-sf-primary/15 bg-white px-4 py-2.5 text-sm font-semibold text-sf-dark shadow-sm transition-colors hover:bg-sf-primary-light disabled:cursor-not-allowed disabled:opacity-50"
            >
              {microsoftSubmitting ? (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-sf-primary/20 border-t-sf-primary" />
              ) : (
                <span className="grid h-4 w-4 grid-cols-2 gap-0.5" aria-hidden="true">
                  <span className="bg-[#f25022]" />
                  <span className="bg-[#7fba00]" />
                  <span className="bg-[#00a4ef]" />
                  <span className="bg-[#ffb900]" />
                </span>
              )}
              {microsoftSubmitting ? 'Signing in with Microsoft...' : 'Sign in with Microsoft'}
            </button>
            {!microsoftSupported && (
              <p className="text-center text-[11px] leading-relaxed text-sf-gray">
                Microsoft single sign-on needs a supported Outlook host. Use email sign-in below.
              </p>
            )}
            {microsoftSupported && (
              <button
                type="button"
                onClick={() => {
                  setShowFallback((open) => !open);
                  setNotice(null);
                  setPassword('');
                }}
                className="self-center text-[11px] font-medium text-sf-primary hover:underline"
              >
                {showFallback ? 'Hide email sign-in' : 'Use email instead'}
              </button>
            )}
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-sf-error/20 bg-sf-error/5 px-3 py-2 text-xs font-medium leading-relaxed text-sf-error"
          >
            {error}
          </div>
        )}
        {notice && (
          <div className="rounded-lg border border-sf-primary/20 bg-sf-primary-light px-3 py-2 text-xs font-medium leading-relaxed text-sf-primary">
            {notice}
          </div>
        )}

        {/* ── Form ── */}
        {fallbackVisible && (
          <form className="flex flex-col gap-3.5" onSubmit={handleSubmit}>
            {microsoftConfigured && (
              <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-sf-gray/70">
                <span className="h-px flex-1 bg-sf-primary/10" />
                <span>Email fallback</span>
                <span className="h-px flex-1 bg-sf-primary/10" />
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="si-email" className={labelClass}>
                Email
              </label>
              <input
                id="si-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@merchant.com"
                autoComplete="username"
                disabled={submitting}
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="si-pass" className={labelClass}>
                  Password
                </label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={submitting}
                  className="text-[11px] font-medium text-sf-primary hover:underline disabled:opacity-50"
                >
                  Forgot password?
                </button>
              </div>
              <input
                id="si-pass"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                disabled={submitting}
                className={inputClass}
              />
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="mt-0.5 flex items-center justify-center gap-2 rounded-lg bg-sf-primary px-4 py-2.5 text-sm font-semibold text-sf-secondary transition-colors hover:bg-sf-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              {submitting && (
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-sf-secondary/30 border-t-sf-secondary" />
              )}
              {submitting ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
